import { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterPopoverPill, Pill, ResultCount, type FilterPopoverPillOption } from '@jywer/ui';
import { fetchAdminSubscriptions } from './api';
import { EditSubscriptionModal } from './EditSubscriptionModal';
import {
  BILLING_PERIOD_LABEL,
  SUBSCRIPTION_STATUS_LABEL,
  SUBSCRIPTION_TYPE_LABEL,
  TRIGGER_EVENT_LABEL,
  type AdminSubscriptionRow,
  type SubscriptionStatus,
  type SubscriptionType,
} from './types';

const TYPE_OPTIONS: FilterPopoverPillOption<SubscriptionType>[] = (
  [
    'basic_hosting_support',
    'seo_aeo',
    'google_ads',
    'meta_ads',
    'linkedin_ads',
    'linkedin_profilpleje',
    'maas',
    'other',
  ] as SubscriptionType[]
).map((v) => ({ value: v, label: SUBSCRIPTION_TYPE_LABEL[v] }));

const STATUS_OPTIONS: FilterPopoverPillOption<SubscriptionStatus>[] = (
  ['pending_setup', 'active', 'paused', 'cancelling', 'cancelled'] as SubscriptionStatus[]
).map((v) => ({ value: v, label: SUBSCRIPTION_STATUS_LABEL[v] }));

const TRIGGER_FIRED_OPTIONS: FilterPopoverPillOption<'yes' | 'no'>[] = [
  { value: 'no', label: 'Afventer' },
  { value: 'yes', label: 'Fyret' },
];

function formatDkk(value: string): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return n.toLocaleString('da-DK', {
    style: 'currency',
    currency: 'DKK',
    maximumFractionDigits: 0,
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function SubscriptionsPage() {
  const [rows, setRows] = useState<AdminSubscriptionRow[]>([]);
  const [total, setTotal] = useState(0);
  const [types, setTypes] = useState<SubscriptionType[]>([]);
  const [statuses, setStatuses] = useState<SubscriptionStatus[]>(['active', 'pending_setup']);
  const [search, setSearch] = useState('');
  const [triggerFired, setTriggerFired] = useState<('yes' | 'no')[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const triggerFiredFilter: 'yes' | 'no' | 'all' = useMemo(() => {
    if (triggerFired.length === 1) return triggerFired[0]!;
    return 'all';
  }, [triggerFired]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetchAdminSubscriptions({
        type: types,
        status: statuses,
        search,
        triggerFired: triggerFiredFilter,
        page,
        pageSize,
      });
      setRows(r.rows);
      setTotal(r.total);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Ukendt fejl');
    } finally {
      setLoading(false);
    }
  }, [types, statuses, search, triggerFiredFilter, page]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold text-primary">Abonnementer</h1>
      <p className="text-sm text-ink-2">
        Oversigt over alle abonnementer. Klik på en række for at redigere.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <FilterPopoverPill
          label="Type"
          options={TYPE_OPTIONS}
          selected={types}
          onChange={(v) => {
            setTypes(v);
            setPage(1);
          }}
        />
        <FilterPopoverPill
          label="Status"
          options={STATUS_OPTIONS}
          selected={statuses}
          onChange={(v) => {
            setStatuses(v);
            setPage(1);
          }}
        />
        <FilterPopoverPill
          label="Trigger"
          options={TRIGGER_FIRED_OPTIONS}
          selected={triggerFired}
          onChange={(v) => {
            setTriggerFired(v);
            setPage(1);
          }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Søg kunde…"
          className="rounded border border-line-2 bg-paper-0 px-2 py-1 text-sm"
        />
      </div>
      {err ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          {err}
        </div>
      ) : null}
      <ResultCount
        shown={rows.length}
        total={total}
        singularNoun="abonnement"
        pluralNoun="abonnementer"
      />
      {loading && rows.length === 0 ? (
        <p className="text-sm text-ink-2">Henter abonnementer…</p>
      ) : null}
      <table className="w-full text-left text-sm">
        <thead className="border-b border-line-1 text-xs uppercase tracking-wide text-ink-2">
          <tr>
            <th className="py-2">Type</th>
            <th className="py-2">Kunde</th>
            <th className="py-2 text-right">Pris</th>
            <th className="py-2">Periode</th>
            <th className="py-2">Trigger</th>
            <th className="py-2">Status</th>
            <th className="py-2">Oprettet</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line-1">
          {rows.map((r) => (
            <tr key={r.id} className="align-top">
              <td className="py-2 font-medium">{SUBSCRIPTION_TYPE_LABEL[r.subscription_type]}</td>
              <td className="py-2 text-ink-1">{r.client_name ?? '—'}</td>
              <td className="py-2 text-right tabular-nums">{formatDkk(r.monthly_price)}</td>
              <td className="py-2">{BILLING_PERIOD_LABEL[r.billing_period]}</td>
              <td className="py-2">
                {r.trigger_event ? (
                  <span className="inline-flex items-center gap-1">
                    <span>{TRIGGER_EVENT_LABEL[r.trigger_event]}</span>
                    {r.sub_rate_fired_at ? (
                      <Pill variant="success">Fyret</Pill>
                    ) : (
                      <Pill variant="muted">Afventer</Pill>
                    )}
                  </span>
                ) : (
                  <span className="text-ink-3">—</span>
                )}
              </td>
              <td className="py-2">
                <Pill variant={r.status === 'active' ? 'success' : 'muted'}>
                  {SUBSCRIPTION_STATUS_LABEL[r.status]}
                </Pill>
              </td>
              <td className="py-2 text-ink-2">{formatDate(r.created_at)}</td>
              <td className="py-2">
                <button
                  type="button"
                  onClick={() => setEditingId(r.id)}
                  className="rounded border border-line-2 px-2 py-1 text-xs hover:border-primary"
                >
                  Rediger
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {editingId ? (
        <EditSubscriptionModal
          id={editingId}
          onClose={() => setEditingId(null)}
          onSaved={() => {
            setEditingId(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
